import React, { useState, useMemo } from "react";
import { format, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Landmark, FileCheck, Printer, Download, ChevronRight,
  Building2, Wheat, TrendingUp, TrendingDown, Wallet,
  Package, MapPin, Calendar, CheckCircle2, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const bancos = [
  {
    id: "bb",
    nome: "Banco do Brasil",
    cor: "#FFCC00",
    corTexto: "#003366",
    logo: "BB",
    programas: ["Pronaf", "Pronamp", "Custeio Agropecuário", "Investimento Rural"]
  },
  {
    id: "sicredi",
    nome: "Sicredi",
    cor: "#00A651",
    corTexto: "#FFFFFF",
    logo: "SICREDI",
    programas: ["Crédito Rural", "Custeio Agrícola", "Investimento Agro"]
  },
  {
    id: "sicoob",
    nome: "Sicoob",
    cor: "#003366",
    corTexto: "#FFFFFF",
    logo: "SICOOB",
    programas: ["Crédito Rural Sicoob", "Pronaf", "Pronamp"]
  },
  {
    id: "santander",
    nome: "Santander",
    cor: "#EC0000",
    corTexto: "#FFFFFF",
    logo: "SANTANDER",
    programas: ["Custeio Agrícola", "Investimento Agropecuário", "Antecipação de Recebíveis"]
  }
];

export default function RelatorioBancario({
  propriedade,
  lancamentos,
  safras,
  insumos,
  periodo,
  dreData,
  fluxoCaixaData,
  financiamentos = [],
  estoquesGrao = [],
  filtros = {}
}) {
  const [bancoSelecionado, setBancoSelecionado] = useState(null);
  const [mostrarRelatorio, setMostrarRelatorio] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const banco = bancos.find(b => b.id === bancoSelecionado);

  // Cálculos adicionais para o relatório bancário
  const dadosFinanceiros = useMemo(() => {
    let lancamentosFiltrados = lancamentos;
    if (filtros.dataInicio && filtros.dataFim) {
      lancamentosFiltrados = lancamentos.filter(l => l.data >= filtros.dataInicio && l.data <= filtros.dataFim);
    } else {
      const mesesAtras = parseInt(periodo);
      const dataLimite = subMonths(new Date(), mesesAtras);
      lancamentosFiltrados = lancamentos.filter(l => new Date(l.data) >= dataLimite);
    }

    const receitaTotal = lancamentosFiltrados
      .filter(l => l.tipo === 'receita')
      .reduce((acc, l) => acc + (l.valor || 0), 0);

    const despesaTotal = lancamentosFiltrados
      .filter(l => l.tipo === 'despesa')
      .reduce((acc, l) => acc + (l.valor || 0), 0);

    const mesesConsiderados = filtros.dataInicio && filtros.dataFim
      ? Math.max(1, (new Date(filtros.dataFim).getFullYear() - new Date(filtros.dataInicio).getFullYear()) * 12 + (new Date(filtros.dataFim).getMonth() - new Date(filtros.dataInicio).getMonth()) + 1)
      : parseInt(periodo);
    const receitaMedia = receitaTotal / mesesConsiderados;
    const despesaMedia = despesaTotal / mesesConsiderados;

    const valorEstoque = insumos.reduce((acc, i) =>
      acc + (i.quantidade_estoque || 0) * (i.custo_unitario || 0), 0
    );

    // Conversão KG -> SCS para valor de estoque (Usando saldo real da View)
    const totalSacasEstoque = estoquesGrao.reduce((acc, e) => acc + (Number(e.quantidade_real) || 0), 0) / 60;
    const valorEstoqueGraos = totalSacasEstoque * 120; // Valor médio de mercado estimado (pode ser ajustado)

    const saldoDevedor = financiamentos
      .filter(f => f.status === 'ativo')
      .reduce((acc, f) => acc + (f.saldo_devedor || f.valor_total || 0), 0);

    // Calcular capacidade de pagamento
    const saldoMedio = receitaMedia - despesaMedia;
    const capacidadePagamento = saldoMedio * 0.7; // 70% do saldo disponível

    // Histórico produtivo - Conversão real de producaoo_real (KG -> SCS)
    const safrasFinalizadas = safras.filter(s => s.status === 'finalizada');
    const produtividadeMedia = safrasFinalizadas.length > 0
      ? safrasFinalizadas.reduce((acc, s) => {
        const prodSacas = (Number(s.producao_real) || 0) / 60;
        return acc + (prodSacas / (Number(s.area_plantada) || 1));
      }, 0) / safrasFinalizadas.length
      : 0;

    return {
      receitaTotal,
      despesaTotal,
      resultado: receitaTotal - despesaTotal,
      receitaMedia,
      despesaMedia,
      saldoMedio,
      capacidadePagamento,
      valorEstoque,
      valorEstoqueGraos,
      patrimonioTotal: valorEstoque + valorEstoqueGraos,
      saldoDevedor,
      produtividadeMedia,
      totalSacasEstoque,
      safrasFinalizadas: safrasFinalizadas.length,
      safrasAtivas: safras.filter(s => s.status === 'em_andamento').length
    };
  }, [lancamentos, safras, insumos, estoquesGrao, financiamentos, periodo, filtros]);

  const handlePrint = () => {
    window.print();
  };

  const gerarScoreAgro = () => {
    let score = 50; // Base

    // Resultado positivo (+20)
    if (dadosFinanceiros.resultado > 0) score += 20;

    // Margem > 20% (+15)
    if (dadosFinanceiros.receitaTotal > 0 &&
      (dadosFinanceiros.resultado / dadosFinanceiros.receitaTotal) > 0.2) score += 15;

    // Histórico produtivo (+10)
    if (dadosFinanceiros.safrasFinalizadas >= 2) score += 10;

    // Patrimônio em estoque (+5)
    if (dadosFinanceiros.valorEstoque > 50000) score += 5;

    return Math.min(score, 100);
  };

  const score = gerarScoreAgro();
  const scoreClass = score >= 70 ? 'text-green-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = score >= 70 ? 'bg-green-100' : score >= 50 ? 'bg-amber-100' : 'bg-red-100';

  if (!mostrarRelatorio) {
    return (
      <div className="space-y-6">
        {/* Seleção de Banco */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-indigo-600" />
              Selecione o Banco para Gerar o Relatório
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup value={bancoSelecionado} onValueChange={setBancoSelecionado}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bancos.map((b) => (
                  <label
                    key={b.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${bancoSelecionado === b.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-stone-200 hover:border-stone-300'
                      }`}
                  >
                    <RadioGroupItem value={b.id} id={b.id} />
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center font-bold text-sm"
                      style={{ backgroundColor: b.cor, color: b.corTexto }}
                    >
                      {b.logo}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-stone-800">{b.nome}</p>
                      <p className="text-xs text-stone-500 mt-1">
                        {b.programas.slice(0, 2).join(" • ")}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </RadioGroup>

            <div className="mt-6 flex justify-end">
              <Button
                disabled={!bancoSelecionado}
                onClick={() => setMostrarRelatorio(true)}
                className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2"
              >
                Gerar Relatório
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview Score */}
        <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg text-stone-800">Seu Score Agro Estimado</h3>
                <p className="text-sm text-stone-600 mt-1">
                  Indicador de saúde financeira para análise de crédito
                </p>
              </div>
              <div className={`text-center p-4 rounded-2xl ${scoreBg}`}>
                <p className={`text-4xl font-bold ${scoreClass}`}>{score}</p>
                <p className="text-xs text-stone-600 mt-1">de 100</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-white/60 rounded-lg">
                <p className="text-xs text-stone-500">Resultado</p>
                <p className={`font-bold ${dadosFinanceiros.resultado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {dadosFinanceiros.resultado >= 0 ? '✓' : '✗'}
                </p>
              </div>
              <div className="text-center p-3 bg-white/60 rounded-lg">
                <p className="text-xs text-stone-500">Histórico</p>
                <p className="font-bold text-stone-800">{dadosFinanceiros.safrasFinalizadas} safras</p>
              </div>
              <div className="text-center p-3 bg-white/60 rounded-lg">
                <p className="text-xs text-stone-500">Cap. Pagamento</p>
                <p className="font-bold text-stone-800">{formatCurrency(dadosFinanceiros.capacidadePagamento)}/mês</p>
              </div>
              <div className="text-center p-3 bg-white/60 rounded-lg">
                <p className="text-xs text-stone-500">Patrimônio</p>
                <p className="font-bold text-stone-800">{formatCurrency(dadosFinanceiros.patrimonioTotal)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Relatório Completo
  return (
    <div className="space-y-4">
      {/* Ações */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => setMostrarRelatorio(false)}>
          ← Voltar
        </Button>
        <Button onClick={handlePrint} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
          <Printer className="w-4 h-4" />
          Imprimir Relatório
        </Button>
      </div>

      {/* Relatório */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden print:shadow-none">
        {/* Cabeçalho do Banco */}
        <div
          className="p-6 text-white print:text-black"
          style={{ backgroundColor: banco?.cor }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center font-bold bg-white"
                style={{ color: banco?.cor }}
              >
                {banco?.logo}
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: banco?.corTexto }}>
                  Relatório de Viabilidade Econômico-Financeira
                </h2>
                <p style={{ color: banco?.corTexto, opacity: 0.8 }}>
                  Documento para análise de crédito rural - {banco?.nome}
                </p>
              </div>
            </div>
            <div className="text-right" style={{ color: banco?.corTexto }}>
              <p className="text-sm opacity-60">Data de Emissão</p>
              <p className="font-bold">{format(new Date(), "dd/MM/yyyy")}</p>
              <p className="text-sm opacity-60 mt-2">Protocolo</p>
              <p className="font-mono text-sm">{banco?.id.toUpperCase()}-{Date.now().toString(36).toUpperCase()}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* 1. Identificação do Produtor e Propriedade */}
          <section>
            <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b-2 border-stone-200 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              1. IDENTIFICAÇÃO DO PRODUTOR E PROPRIEDADE
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-stone-50 rounded-xl">
                <p className="text-sm text-stone-500">Nome da Propriedade</p>
                <p className="font-bold text-lg">{propriedade?.nome || '-'}</p>
              </div>
              <div className="p-4 bg-stone-50 rounded-xl">
                <p className="text-sm text-stone-500">Localização</p>
                <p className="font-bold text-lg">{propriedade?.localizacao || '-'}</p>
              </div>
              <div className="p-4 bg-stone-50 rounded-xl">
                <p className="text-sm text-stone-500">Área Total</p>
                <p className="font-bold text-lg">{propriedade?.area_total ? `${propriedade.area_total} hectares` : '-'}</p>
              </div>
              <div className="p-4 bg-stone-50 rounded-xl">
                <p className="text-sm text-stone-500">Cadastro Ambiental Rural (CAR)</p>
                <p className="font-bold text-lg">{propriedade?.car || '-'}</p>
              </div>
              <div className="p-4 bg-stone-50 rounded-xl col-span-2">
                <p className="text-sm text-stone-500">Atividades Desenvolvidas</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {propriedade?.tipo_atividade?.map((at) => (
                    <Badge key={at} className="bg-[#40916C]/20 text-[#2D6A4F]">
                      {at}
                    </Badge>
                  )) || <span className="text-stone-400">-</span>}
                </div>
              </div>
            </div>
          </section>

          {/* 2. Score Agro */}
          <section>
            <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b-2 border-stone-200 flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-indigo-600" />
              2. SCORE AGRO - INDICADOR DE SAÚDE FINANCEIRA
            </h3>
            <div className={`p-6 rounded-xl ${scoreBg} flex items-center justify-between`}>
              <div>
                <p className="font-bold text-lg text-stone-800">Classificação do Produtor</p>
                <p className="text-stone-600 mt-1">
                  {score >= 70 ? 'BAIXO RISCO - Excelente histórico financeiro e produtivo' :
                    score >= 50 ? 'RISCO MODERADO - Bom histórico com pontos de atenção' :
                      'RISCO ELEVADO - Necessita análise detalhada'}
                </p>
                <div className="flex gap-4 mt-3">
                  <div className="flex items-center gap-2">
                    {dadosFinanceiros.resultado >= 0 ?
                      <CheckCircle2 className="w-4 h-4 text-green-600" /> :
                      <AlertCircle className="w-4 h-4 text-red-600" />}
                    <span className="text-sm">Resultado Positivo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {dadosFinanceiros.safrasFinalizadas >= 2 ?
                      <CheckCircle2 className="w-4 h-4 text-green-600" /> :
                      <AlertCircle className="w-4 h-4 text-amber-600" />}
                    <span className="text-sm">Histórico Produtivo</span>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <div className={`w-24 h-24 rounded-full ${scoreBg} border-4 flex items-center justify-center`} style={{ borderColor: score >= 70 ? '#16a34a' : score >= 50 ? '#d97706' : '#dc2626' }}>
                  <span className={`text-4xl font-bold ${scoreClass}`}>{score}</span>
                </div>
                <p className="text-xs text-stone-500 mt-2">PONTOS</p>
              </div>
            </div>
          </section>

          {/* 3. Resumo Financeiro */}
          <section>
            <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b-2 border-stone-200 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-indigo-600" />
              3. DEMONSTRATIVO FINANCEIRO (Últimos {periodo} meses)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-700 font-medium">RECEITAS</p>
                  </div>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(dadosFinanceiros.receitaTotal)}</p>
                  <p className="text-xs text-green-600 mt-1">Média mensal: {formatCurrency(dadosFinanceiros.receitaMedia)}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    <p className="text-sm text-red-700 font-medium">DESPESAS</p>
                  </div>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(dadosFinanceiros.despesaTotal)}</p>
                  <p className="text-xs text-red-600 mt-1">Média mensal: {formatCurrency(dadosFinanceiros.despesaMedia)}</p>
                </CardContent>
              </Card>
              <Card className={`${dadosFinanceiros.resultado >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-stone-50 border-stone-200'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wallet className="w-5 h-5 text-blue-600" />
                    <p className="text-sm text-stone-700 font-medium">RESULTADO</p>
                  </div>
                  <p className={`text-2xl font-bold ${dadosFinanceiros.resultado >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                    {formatCurrency(dadosFinanceiros.resultado)}
                  </p>
                  <p className="text-xs text-stone-600 mt-1">
                    Margem: {dadosFinanceiros.receitaTotal > 0 ? ((dadosFinanceiros.resultado / dadosFinanceiros.receitaTotal) * 100).toFixed(1) : 0}%
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Detalhamento por categoria */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-green-700 mb-3">Receitas por Categoria</h4>
                <div className="space-y-2">
                  {dreData?.receitas?.slice(0, 5).map(([cat, valor]) => (
                    <div key={cat} className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">{cat}</span>
                      <span className="font-medium text-green-600">{formatCurrency(valor)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-red-700 mb-3">Despesas por Categoria</h4>
                <div className="space-y-2">
                  {dreData?.despesas?.slice(0, 5).map(([cat, valor]) => (
                    <div key={cat} className="flex justify-between py-2 border-b border-stone-100">
                      <span className="text-stone-600">{cat}</span>
                      <span className="font-medium text-red-600">{formatCurrency(valor)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 4. Fluxo de Caixa Projetado */}
          <section>
            <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b-2 border-stone-200 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              4. FLUXO DE CAIXA - HISTÓRICO MENSAL
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-stone-200">
                    <th className="text-left py-2 px-3 font-bold">Mês</th>
                    <th className="text-right py-2 px-3 font-bold text-green-700">Entradas</th>
                    <th className="text-right py-2 px-3 font-bold text-red-700">Saídas</th>
                    <th className="text-right py-2 px-3 font-bold">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {fluxoCaixaData?.slice(-6).map((mes) => (
                    <tr key={mes.mes} className="border-b border-stone-100">
                      <td className="py-2 px-3 font-medium">{mes.mes}</td>
                      <td className="py-2 px-3 text-right text-green-600">{formatCurrency(mes.receitas)}</td>
                      <td className="py-2 px-3 text-right text-red-600">{formatCurrency(mes.despesas)}</td>
                      <td className={`py-2 px-3 text-right font-bold ${mes.saldo >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(mes.saldo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 5. Histórico de Produção */}
          <section>
            <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b-2 border-stone-200 flex items-center gap-2">
              <Wheat className="w-5 h-5 text-indigo-600" />
              5. HISTÓRICO DE PRODUÇÃO E SAFRAS
            </h3>
            {safras.length === 0 ? (
              <p className="text-stone-500 text-center py-4">Nenhuma safra cadastrada</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-stone-200 bg-stone-50">
                      <th className="text-left py-3 px-3 font-bold">Safra</th>
                      <th className="text-left py-3 px-3 font-bold">Cultura</th>
                      <th className="text-right py-3 px-3 font-bold">Área (ha)</th>
                      <th className="text-right py-3 px-3 font-bold">Produção</th>
                      <th className="text-right py-3 px-3 font-bold">Produtividade</th>
                      <th className="text-center py-3 px-3 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safras.map(s => {
                      const prodReal = (Number(s.producao_real) || 0) / 60;
                      const prodEsperada = (Number(s.producao_esperada) || 0) / 60;
                      const produtividade = s.area_plantada ? (prodReal > 0 ? (prodReal / Number(s.area_plantada)).toFixed(1) : (prodEsperada / Number(s.area_plantada)).toFixed(1)) : '-';
                      return (
                        <tr key={s.id} className="border-b border-stone-100">
                          <td className="py-2 px-3 font-medium">{s.nome}</td>
                          <td className="py-2 px-3">
                            <Badge variant="secondary">{s.cultura}</Badge>
                          </td>
                          <td className="py-2 px-3 text-right">{s.area_plantada || '-'}</td>
                          <td className="py-2 px-3 text-right">
                            {prodReal > 0 ? `${prodReal.toLocaleString('pt-BR')} sc` : (prodEsperada > 0 ? `${prodEsperada.toLocaleString('pt-BR')} sc` : '-')}
                          </td>
                          <td className="py-2 px-3 text-right font-medium">{produtividade !== '-' ? `${produtividade} sc/ha` : '-'}</td>
                          <td className="py-2 px-3 text-center">
                            <Badge className={
                              s.status === 'finalizada' ? 'bg-green-100 text-green-700' :
                                s.status === 'em_andamento' ? 'bg-blue-100 text-blue-700' :
                                  'bg-stone-100 text-stone-700'
                            }>
                              {s.status === 'finalizada' ? 'Finalizada' :
                                s.status === 'em_andamento' ? 'Em Andamento' : 'Planejada'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* 6. Patrimônio e Estoque */}
          <section>
            <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b-2 border-stone-200 flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" />
              6. PATRIMÔNIO EM ESTOQUE
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-stone-500">Estoque Insumos</p>
                  <p className="text-xl font-bold text-stone-800 mt-1">{formatCurrency(dadosFinanceiros.valorEstoque)}</p>
                  <p className="text-xs text-stone-400">{insumos.length} itens</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-stone-500">Estoque Grãos</p>
                  <p className="text-xl font-bold text-amber-700 mt-1">{formatCurrency(dadosFinanceiros.valorEstoqueGraos)}</p>
                  <p className="text-xs text-stone-400">{dadosFinanceiros.totalSacasEstoque.toLocaleString('pt-BR')} sacas (real)</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-green-600">Patrimônio Total</p>
                  <p className="text-xl font-bold text-green-700 mt-1">{formatCurrency(dadosFinanceiros.patrimonioTotal)}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-red-600">Saldo Devedor</p>
                  <p className="text-xl font-bold text-red-700 mt-1">{formatCurrency(dadosFinanceiros.saldoDevedor)}</p>
                  <p className="text-xs text-red-500">{financiamentos.filter(f => f.status === 'ativo').length} financiamentos</p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* 7. Capacidade de Pagamento */}
          <section>
            <h3 className="font-bold text-lg text-stone-800 mb-4 pb-2 border-b-2 border-stone-200 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              7. CAPACIDADE DE PAGAMENTO
            </h3>
            <div className={`p-6 rounded-xl ${dadosFinanceiros.capacidadePagamento > 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-sm text-stone-600">Saldo Médio Mensal</p>
                  <p className="text-2xl font-bold text-stone-800 mt-1">{formatCurrency(dadosFinanceiros.saldoMedio)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-stone-600">Capacidade de Pagamento Mensal</p>
                  <p className={`text-2xl font-bold mt-1 ${dadosFinanceiros.capacidadePagamento > 0 ? 'text-green-700' : 'text-amber-700'}`}>
                    {formatCurrency(dadosFinanceiros.capacidadePagamento)}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">(70% do saldo disponível)</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-stone-600">Capacidade Anual</p>
                  <p className="text-2xl font-bold text-stone-800 mt-1">{formatCurrency(dadosFinanceiros.capacidadePagamento * 12)}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Rodapé / Assinaturas */}
          <section className="pt-8 border-t-2 border-stone-300">
            <div className="grid grid-cols-2 gap-12 mt-8">
              <div className="text-center">
                <div className="border-t border-stone-400 pt-2 mt-16">
                  <p className="font-medium">Assinatura do Produtor Rural</p>
                  <p className="text-sm text-stone-500">CPF: ___________________</p>
                </div>
              </div>
              <div className="text-center">
                <div className="border-t border-stone-400 pt-2 mt-16">
                  <p className="font-medium">Responsável Técnico / Contador</p>
                  <p className="text-sm text-stone-500">CRC: ___________________</p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-stone-100 rounded-lg text-center text-xs text-stone-500">
              <p>Documento gerado automaticamente pelo sistema AgroGestão em {format(new Date(), "dd/MM/yyyy 'às' HH:mm")}</p>
              <p className="mt-1">As informações contidas neste relatório são de responsabilidade do produtor rural declarante.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
